import { siteConfig } from "../config/siteConfig";
import { UtensilsCrossed, Wine } from "lucide-react";

export default function RestaurantBar() {
  const { restaurantBar, contact } = siteConfig;
  const waLink = `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
    `Hello THURULIYA HERITAGE, I'd like to reserve at the Restaurant & Bar.`
  )}`;

  return (
    <section
      id="restaurant-bar"
      data-testid="restaurant-bar-section"
      className="section bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 reveal">
          <p className="font-overline mb-4">Dine & Unwind</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A362D] mb-4 leading-tight">
            {restaurantBar.title}
          </h2>
          <div className="gold-divider"></div>
          <p className="text-[#2C2C2C] max-w-3xl mx-auto mt-6 text-base sm:text-lg">
            {restaurantBar.description}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {/* Big hero image */}
          <div className="lg:col-span-7 lg:row-span-2 relative overflow-hidden rounded-3xl group min-h-[420px]">
            <img
              src={restaurantBar.images.hero}
              alt="Restaurant Interior"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-10 text-[#FAF9F6]">
              <div className="flex items-center gap-3 mb-3">
                <UtensilsCrossed className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                  Restaurant
                </span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl mb-2">
                {restaurantBar.restaurant.title}
              </h3>
              <p className="font-display italic text-[#D4B06A] text-lg mb-5">
                {restaurantBar.restaurant.tagline}
              </p>
              <div className="flex flex-wrap gap-2">
                {restaurantBar.restaurant.offerings.slice(0, 6).map((o) => (
                  <span
                    key={o}
                    className="chip chip-dark"
                  >
                    {o}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Food close-up */}
          <div className="lg:col-span-5 relative overflow-hidden rounded-3xl group min-h-[200px]">
            <img
              src={restaurantBar.images.food}
              alt="Sri Lankan Cuisine"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-[#FAF9F6]">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-1">
                Heritage Cuisine
              </p>
              <p className="font-display text-2xl">Sri Lankan & Continental</p>
            </div>
          </div>

          {/* Bar block */}
          <div
            className="lg:col-span-5 relative overflow-hidden rounded-3xl group min-h-[200px]"
            data-testid="bar-card"
          >
            <img
              src={restaurantBar.images.bar}
              alt="Moonstone Bar"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-[#FAF9F6]">
              <div className="flex items-center gap-3 mb-2">
                <Wine className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                  Bar
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mb-1">
                {restaurantBar.bar.title}
              </h3>
              <p className="font-display italic text-[#D4B06A] text-sm">
                {restaurantBar.bar.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* Combined offerings + CTA */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 bg-[#F3EFE6] rounded-3xl p-7 sm:p-9">
            <p className="font-overline mb-4">From the Bar</p>
            <div className="flex flex-wrap gap-2">
              {restaurantBar.bar.offerings.map((o) => (
                <span key={o} className="chip">
                  {o}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-[#1A362D] rounded-3xl p-7 sm:p-9 flex flex-col justify-between text-[#FAF9F6]">
            <div>
              <p className="font-overline text-[#C5A059] mb-3">Reservations</p>
              <p className="font-display text-2xl leading-snug">
                Whether for a quiet dinner or evening drinks — we'd love to host you.
              </p>
            </div>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary mt-6 self-start"
              data-testid="restaurant-bar-reserve-btn"
            >
              Reserve via WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
